import KanbanAPI from '../../api/kanban';

export const state = {
  boards: [],
  activeBoardId: '',
  uiFlags: {
    isFetching: false,
    isFetchingBoard: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
  },
};

export const getters = {
  getBoards: _state => _state.boards,
  getBoardByPublicId: _state => publicId =>
    _state.boards.find(board => board.public_id === publicId) || {},
  getUIFlags: _state => _state.uiFlags,
  getSelectedBoard: _state => {
    return (
      _state.boards.find(board => board.is_default) || _state.boards[0] || {}
    );
  },
};

export const actions = {
  getBoards: async ({ commit }) => {
    commit('SET_KANBAN_UI_FLAG', { isFetching: true });
    try {
      const response = await KanbanAPI.getBoards();
      commit('SET_KANBAN_BOARDS', response.data.payload || []);
    } finally {
      commit('SET_KANBAN_UI_FLAG', { isFetching: false });
    }
  },
  getBoard: async ({ commit }, publicId) => {
    commit('SET_KANBAN_UI_FLAG', { isFetchingBoard: true });
    try {
      const response = await KanbanAPI.getBoard(publicId);
      commit('UPSERT_KANBAN_BOARD', response.data);
      return response.data;
    } finally {
      commit('SET_KANBAN_UI_FLAG', { isFetchingBoard: false });
    }
  },
  refreshActiveBoard: async ({ dispatch, state: _state }) => {
    if (!_state.activeBoardId) {
      return null;
    }

    return dispatch('getBoard', _state.activeBoardId);
  },
  setupFunnel: async ({ commit, dispatch }) => {
    const response = await KanbanAPI.setupFunnel();
    await dispatch('getBoards');
    commit('SET_ACTIVE_BOARD_ID', response.data.public_id || '');
    if (response.data.public_id) {
      await dispatch('getBoard', response.data.public_id);
    }
    return response.data;
  },
  createBoard: async ({ commit, dispatch }, payload) => {
    const response = await KanbanAPI.createBoard(payload);
    await dispatch('getBoards');
    commit('SET_ACTIVE_BOARD_ID', response.data.public_id || '');
    if (response.data.public_id) {
      await dispatch('getBoard', response.data.public_id);
    }
    return response.data;
  },
  updateBoard: async ({ dispatch }, payload) => {
    const response = await KanbanAPI.updateBoard(payload.public_id, payload);
    await dispatch('getBoards');
    await dispatch('getBoard', payload.public_id);
    return response.data;
  },
  deleteBoard: async ({ commit, dispatch, state: _state }, publicId) => {
    await KanbanAPI.deleteBoard(publicId);
    await dispatch('getBoards');
    if (_state.activeBoardId === publicId) {
      commit('SET_ACTIVE_BOARD_ID', '');
    }
  },
  migrateBoard: async ({ dispatch }, { sourceBoardPublicId, ...payload }) => {
    const response = await KanbanAPI.migrateBoard(sourceBoardPublicId, payload);
    await dispatch('getBoards');
    return response.data;
  },
  createColumn: async ({ dispatch }, payload) => {
    const response = await KanbanAPI.createColumn(payload);
    await dispatch('refreshActiveBoard');
    return response.data;
  },
  updateColumn: async ({ dispatch }, payload) => {
    await KanbanAPI.updateColumn(payload.public_id, payload);
    await dispatch('refreshActiveBoard');
  },
  deleteColumn: async ({ dispatch }, publicId) => {
    await KanbanAPI.deleteColumn(publicId);
    await dispatch('refreshActiveBoard');
  },
  reorderColumns: async ({ dispatch }, columns) => {
    await KanbanAPI.reorderColumns(columns);
    await dispatch('refreshActiveBoard');
  },
  createCard: async ({ dispatch }, payload) => {
    await KanbanAPI.createCard(payload);
    await dispatch('refreshActiveBoard');
  },
  updateCard: async ({ dispatch }, payload) => {
    await KanbanAPI.updateCard(payload.public_id, payload);
    await dispatch('refreshActiveBoard');
  },
  moveCard: async ({ dispatch }, payload) => {
    await KanbanAPI.moveCard(payload.public_id, payload);
    await dispatch('refreshActiveBoard');
  },
  deleteCard: async ({ dispatch }, publicId) => {
    await KanbanAPI.deleteCard(publicId);
    await dispatch('refreshActiveBoard');
  },
  getCard: async (_context, publicId) => {
    const response = await KanbanAPI.getCard(publicId);
    return response.data;
  },
  getActivities: async (_context, cardPublicId) => {
    const response = await KanbanAPI.getActivities(cardPublicId);
    return response.data.payload || [];
  },
  createActivity: async (_context, { cardPublicId, body }) => {
    const response = await KanbanAPI.createActivity(cardPublicId, body);
    return response.data.payload;
  },
  deleteActivity: async (_context, { cardPublicId, activityPublicId }) => {
    await KanbanAPI.deleteActivity(cardPublicId, activityPublicId);
  },
  getTasks: async (_context, cardPublicId) => {
    const response = await KanbanAPI.getTasks(cardPublicId);
    return response.data.payload || [];
  },
  createTask: async (_context, { cardPublicId, title }) => {
    const response = await KanbanAPI.createTask(cardPublicId, title);
    return response.data.payload;
  },
  updateTask: async (_context, { cardPublicId, taskPublicId, data }) => {
    const response = await KanbanAPI.updateTask(
      cardPublicId,
      taskPublicId,
      data
    );
    return response.data.payload;
  },
  deleteTask: async (_context, { cardPublicId, taskPublicId }) => {
    await KanbanAPI.deleteTask(cardPublicId, taskPublicId);
  },
};

export const mutations = {
  SET_KANBAN_UI_FLAG(_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },
  SET_KANBAN_BOARDS(_state, data) {
    const boardMap = new Map(
      _state.boards.map(board => [board.public_id, board])
    );

    _state.boards = data.map(board => {
      const existingBoard = boardMap.get(board.public_id);
      return existingBoard?.columns
        ? { ...board, columns: existingBoard.columns }
        : board;
    });
  },
  UPSERT_KANBAN_BOARD(_state, data) {
    const index = _state.boards.findIndex(
      board => board.public_id === data.public_id
    );

    if (index >= 0) {
      _state.boards.splice(index, 1, data);
      return;
    }

    _state.boards.push(data);
  },
  SET_ACTIVE_BOARD_ID(_state, publicId) {
    _state.activeBoardId = publicId;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
